/*global Sentry*/ //https://github.com/octalmage/gatsby-plugin-sentry/issues/20

import React, { useState, useRef, useEffect } from "react"
import classNames from "classnames"
import { Tooltip } from "react-tippy"

import ArrowLeft from "../../../assets/_V2/svg/ArrowLeft"

import "react-tippy/dist/tippy.css"
import styles from "./styles.module.scss"
import "../../../styles/_V2/components/tooltip.scss"
import { registrationSmsConfirmationEvent } from "../../../gtm-events"
import { pluralize } from "../../../utils/pluralize"

import config from "../../../config"

import { sendSmsCode, checkSmsCode } from "../../../apiRequests/registration"
import { NetError } from "../../../apiRequests/utils"

export enum ACTION_STATUSES {
	SENDING = "SENDING",
	NOT_SEND = "NOT_SEND",
	SUCCESS = "SUCCESS",
	FAIL = "FAIL",
}

function SmsCodeForm(props) {
	const [actionStatus, setActionStatus] = useState<ACTION_STATUSES>(
		ACTION_STATUSES.NOT_SEND
	)
	const [sendAgainTimer, setSendAgainTimer] = useState<number>(60)
	const [showTimer, setShowTimer] = useState<boolean>(true)

	const initDigits = ["", "", "", ""]
	const [digits, setDigits] = useState(initDigits)

	const length = digits.length

	// здесь будут ссылки на input-элементы
	const inputRefs = useRef([])

	useEffect(() => {
		inputRefs.current[0].focus()

		const timer = setInterval(() => {
			setSendAgainTimer(sendAgainTimer => {
				const newValue = sendAgainTimer - 1

				if (newValue <= 0) {
					// clearInterval(timer);
					setShowTimer(false)
				}

				return newValue
			})
		}, 1000)

		attemptToSendSmsCode()

		return () => clearInterval(timer)
	}, [])

	/**
	 * Сделать запрос на отправку смс на переданный телефонный номер
	 */
	const attemptToSendSmsCode = async () => {
		try {
			const smsCodeData = await sendSmsCode(
				props.formData.phone.value,
				props.recaptchaToken,
				props.recaptchaV2Token
			)

			if (smsCodeData.send) {
				setSendAgainTimer(smsCodeData.secondsToRepeat)
			}
		} catch (error) {
			Sentry.captureException(error, scope =>
				scope.setTag("source", "registrationSmsConfirm")
			)

			// Если произошла ошибка на сервере вызвать соответствующий обработчик
			if (error instanceof NetError) {
				props.netErrorHandler()
			}
		}
	}

	const handleKeyDown = (index, event) => {
		if (event.key === "Backspace") {
			event.preventDefault()
			setActionStatus(ACTION_STATUSES.NOT_SEND)

			if (digits[index].match(/^[0-9]$/)) {
				// если элемент массива digits содержит цифру, то при нажатии клавиши
				// вызываем callback-функцию родителя, чтобы записать пустую строку
				const newDigits = [...digits] // копия digits
				newDigits[index] = ""
				setDigits(newDigits)
			} else {
				// элемент массива digits пустой, удалять нечего — так что надо сместить
				// фокус на предыдущее поле input — при условии, что это не первое поле
				if (index > 0) inputRefs.current[index - 1].focus()
			}
		}
	}

	const handleChange = async (index: number, newValue: string) => {
		setActionStatus(ACTION_STATUSES.NOT_SEND)
		const oldDigit = digits[index]

		// старую цифру из поля ввода убираем, оставляя только новую
		const newDigit = newValue.trim().replace(oldDigit, "")

		// если это не цифра, ничего не делаем, пока не будет цифры
		if (newDigit < "0" || newDigit > "9") return

		// обновляем digits
		const newDigits = [...digits] // копия digits
		newDigits[index] = newDigit
		setDigits(newDigits)

		// смещаем фокус на следующее поле для ввода следующей цифры
		if (index < length - 1) {
			inputRefs.current[index + 1].focus()
		} else {
			// или убираем фокус, если это было последнее поле
			inputRefs.current[index].blur()

			if (newDigits.join("").length === 4) {
				await handleSubmit(newDigits.join(""))
			}
		}
	}

	const handleSubmit = async (code: string) => {
		try {
			const isSmsCodeValid = await checkSmsCode(
				code,
				props.formData.phone.value
			)
			setActionStatus(ACTION_STATUSES.SENDING)

			if (isSmsCodeValid) {
				props.setStep(3, {
					smsCode: code,
				})

				try {
					registrationSmsConfirmationEvent()
				} catch {
					console.log("gtm не найден")
				}

				setActionStatus(ACTION_STATUSES.SUCCESS)
			} else {
				setActionStatus(ACTION_STATUSES.FAIL)
			}
		} catch (error) {
			Sentry.captureException(error, scope =>
				scope.setTag("source", "registrationSmsConfirm")
			)

			// Если произошла ошибка на сервере вызвать соответствующий обработчик
			if (error instanceof NetError) {
				props.netErrorHandler()
			}
		}
	}

	const sendAgain = async () => {
		setSendAgainTimer(60)
		setShowTimer(true)
		setActionStatus(ACTION_STATUSES.NOT_SEND)
		setDigits(initDigits)

		inputRefs.current[0].focus()

		// grecaptcha.ready(function() {
		// 	grecaptcha
		// 		.execute(config.GOOGLE_RECAPTCHA_ID, {
		// 			action: "submit",
		// 		})
		// 		.then(async function(token: string) {
		// 			props.setRecaptchaToken(token)
		//
		// 			await attemptToSendSmsCode()
		// 		})
		// })
	}

	const goBack = () => {
		props.setStep(1)
	}

	return (
		<form className={styles.smsForm} onSubmit={handleSubmit}>
			<div className={styles.smsForm__titleRow}>
				<button
					className={styles.smsForm__backBtn}
					type="button"
					onClick={() => goBack()}
				>
					<ArrowLeft className={styles.smsForm__backIcon} />
				</button>

				<h3 className={styles.smsForm__title}>Код из смс</h3>
			</div>

			<div className={styles.smsForm__fields}>
				{digits.map((digit, index) => (
					<div className={styles.smsForm__fieldWrapper}>
						<input
							className={classNames(styles.smsForm__field, {
								[styles.smsForm__fieldError]:
									actionStatus === ACTION_STATUSES.FAIL,
							})}
							type="number"
							key={index}
							value={digit}
							onChange={event => handleChange(index, event.target.value)}
							onKeyDown={event => handleKeyDown(index, event)}
							ref={element => (inputRefs.current[index] = element)}
							required
						/>
						<div className={styles.smsForm__fieldOverlay}></div>
					</div>
				))}
			</div>

			<p
				className={classNames(styles.smsForm__error, {
					[styles.smsForm__errorHidden]: actionStatus !== ACTION_STATUSES.FAIL,
				})}
			>
				Неверно введен код
			</p>

			{!showTimer && (
				<a className={styles.smsForm__sendAgain} onClick={() => sendAgain()}>
					Выслать код повторно
				</a>
			)}

			{showTimer && (
				<p className={styles.smsForm__sendAgainTimer}>
					Запросить код повторно можно через {sendAgainTimer}{" "}
					{pluralize("секунду|секунды|секунд", sendAgainTimer)}
				</p>
			)}

			<Tooltip
				html={
					<div>
						Проблемы могут быть со стороны сотового оператора или со стороны
						сервиса отправки сообщений. Обратитесь в службу технической
						поддержки по бесплатному номеру:&nbsp;
						<a href="tel:88005002138">8 (800) 500-21-38</a>
					</div>
				}
				interactive
				arrow={true}
				position="bottom"
				trigger="mouseenter"
			>
				<a className={styles.smsForm__sms}>Не приходит СМС</a>
			</Tooltip>
		</form>
	)
}

export default SmsCodeForm
